import type {
  LifecycleCounts,
  LifecycleDailyDigest,
  LifecycleDigestNew,
  LifecycleDigestAdvanced,
  LifecycleDigestCompleted,
} from '../types/lifecycle';
import {
  formatDate,
  formatDateTime,
  humanizeEventType,
  humanizePublicText,
  signedDelta,
  trackingLevelClass,
  trackingLevelLabel,
} from '../utils/lifecycleFormat';
import { RiderBadge } from './RiderBadge';
import { SequenceBadge } from './SequenceSurfacing';

interface DailyDigestProps {
  digest: LifecycleDailyDigest;
  counts: LifecycleCounts;
  generatedAt: string;
  onSelectAnalysis: (proposalId: string) => void;
  onSelectTracked: (proposalId: string) => void;
  onShowAlertAnalyses: () => void;
}

export function DailyDigest({
  digest,
  counts,
  generatedAt,
  onSelectAnalysis,
  onSelectTracked,
  onShowAlertAnalyses,
}: DailyDigestProps) {
  const isAllEmpty =
    digest.new_today.length === 0 &&
    digest.advanced_to_analysis_today.length === 0 &&
    digest.analysis_completed_today.length === 0;
  const attentionCount = new Set(
    digest.analysis_completed_today
      .filter((item) => item.alert === 'yes' || item.final_risk_level === 'high')
      .map((item) => item.proposal_id)
  ).size;

  return (
    <div className="daily-digest">
      <div className="daily-digest__header">
        <p className="daily-digest__date" aria-label="Datum">
          {formatDate(digest.date)}
        </p>
        <p className="daily-digest__summary">
          {humanizePublicText(digest.summary_text) || 'Dnes pro tento den nemáme připravené shrnutí.'}
        </p>
      </div>

      <div className="metric-cards">
        <MetricCard
          label={'Nové návrhy dnes'}
          value={digest.new_today.length}
          sublabel={'Nově zachycené tisky'}
          accent="blue"
          icon="plus"
          actionLabel={'Přejít na nové návrhy'}
          onClick={digest.new_today.length > 0 ? () => scrollToDigestSection('digest-new-today') : undefined}
        />
        <MetricCard
          label={'Postoupilo do analýzy'}
          value={digest.advanced_to_analysis_today.length}
          sublabel={'Čekají na hlubší posouzení'}
          accent="amber"
          icon="arrow"
          actionLabel={'Přejít na návrhy v analýze'}
          onClick={digest.advanced_to_analysis_today.length > 0 ? () => scrollToDigestSection('digest-advanced-today') : undefined}
        />
        <MetricCard
          label={'Hotová analýza dnes'}
          value={digest.analysis_completed_today.length}
          sublabel={'S hotovým verdiktem'}
          accent="green"
          icon="check"
          actionLabel={'Přejít na hotové analýzy'}
          onClick={digest.analysis_completed_today.length > 0 ? () => scrollToDigestSection('digest-completed-today') : undefined}
        />
        <MetricCard
          label={'Vyžaduje pozornost'}
          value={attentionCount}
          sublabel={attentionCount === 0 ? 'dnes nic vážného' : undefined}
          accent={attentionCount > 0 ? 'red' : 'grey'}
          icon="alert"
          actionLabel={'Zobrazit analýzy s upozorněním'}
          onClick={attentionCount > 0 ? onShowAlertAnalyses : undefined}
        />
      </div>
      <p className="daily-digest__longterm">
        {'Dlouhodobě sleduje'} <strong>{counts.total_tracked}</strong> {'návrhů'}
        {counts.active_tracked !== counts.total_tracked && <> ({counts.active_tracked} {'aktivních'})</>}
        .
      </p>

      {isAllEmpty ? (
        <div className="daily-digest__empty" role="status">
          <h3>Dnes systém nezachytil žádný nový návrh ani neproběhla žádná analýza.</h3>
          <p>Sněmovna zasedá pondělí–pátek. Zkontrolujte zítra po 18:00.</p>
          <p className="daily-digest__empty-meta">
            Poslední generování feedu: {formatDateTime(generatedAt)}
          </p>
        </div>
      ) : (
        <>
          {digest.new_today.length > 0 && (
            <section className="daily-digest__section" id="digest-new-today">
              <h3>{'Co dnes přibylo do sledování'}</h3>
              <ul className="daily-digest__list">
                {digest.new_today.map((item) => (
                  <NewProposalCard key={item.proposal_id} item={item} onSelect={() => onSelectTracked(item.proposal_id)} />
                ))}
              </ul>
            </section>
          )}

          {digest.advanced_to_analysis_today.length > 0 && (
            <section className="daily-digest__section" id="digest-advanced-today">
              <h3>{'Co dnes postoupilo do analýzy'}</h3>
              <ul className="daily-digest__list">
                {digest.advanced_to_analysis_today.map((item) => (
                  <AdvancedCard key={item.proposal_id + item.enqueued_at} item={item} onSelect={() => onSelectTracked(item.proposal_id)} />
                ))}
              </ul>
            </section>
          )}

          {digest.analysis_completed_today.length > 0 && (
            <section className="daily-digest__section" id="digest-completed-today">
              <h3>{'Hotové analýzy dnes'}</h3>
              <ul className="daily-digest__list">
                {digest.analysis_completed_today.map((item) => (
                  <CompletedCard key={item.proposal_id + item.completed_at} item={item} onSelect={() => onSelectAnalysis(item.proposal_id)} />
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
  sublabel,
  accent,
  icon,
  actionLabel,
  onClick,
}: {
  label: string;
  value: number;
  sublabel?: string;
  accent: 'blue' | 'amber' | 'green' | 'grey' | 'red';
  icon: 'plus' | 'arrow' | 'check' | 'alert';
  actionLabel: string;
  onClick?: () => void;
}) {
  const iconLabel = {
    plus: '+',
    arrow: '→',
    check: '✓',
    alert: '!',
  }[icon];

  const content = (
    <>
      <div className="metric-card__top">
        <span className="metric-card__icon" aria-hidden="true">
          {iconLabel}
        </span>
        <div className="metric-card__value">{value}</div>
      </div>
      <div>
        <div className="metric-card__label">{label}</div>
        {sublabel && <div className="metric-card__sublabel">{sublabel}</div>}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={`metric-card metric-card--${accent} metric-card--clickable`}
        onClick={onClick}
        aria-label={actionLabel}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={`metric-card metric-card--${accent} metric-card--disabled`} aria-label={`${label}: ${value}`}>
      {content}
    </div>
  );
}

function scrollToDigestSection(id: string) {
  if (typeof document === 'undefined') return;
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function NewProposalCard({ item, onSelect }: { item: LifecycleDigestNew; onSelect: () => void }) {
  const hasAssessedRisk = item.risk_level != null;

  return (
    <li className="digest-card">
      <button type="button" className="digest-card__btn" onClick={onSelect}>
        <div className="digest-card__top">
          <span className={`badge ${hasAssessedRisk ? trackingLevelClass(item.risk_level) : 'tracking-level--unknown'}`}>
            {hasAssessedRisk ? trackingLevelLabel(item.risk_level) : 'Neposouzeno'}
          </span>
          {item.possible_rider?.label && <RiderBadge label={item.possible_rider.label} />}
          {item.sequence_surfacing && <SequenceBadge surfacing={item.sequence_surfacing} />}
          {item.risk_score != null && <span className="digest-card__score">{'sk\u00f3re'} {item.risk_score}</span>}
        </div>
        <div className="digest-card__title">{item.title}</div>
        {item.initial_score_reason && <div className="digest-card__reason">{item.initial_score_reason}</div>}
        <div className="digest-card__meta">{'První zachycení:'} {formatDateTime(item.first_seen_at)}</div>
      </button>
    </li>
  );
}

function AdvancedCard({ item, onSelect }: { item: LifecycleDigestAdvanced; onSelect: () => void }) {
  return (
    <li className="digest-card">
      <button type="button" className="digest-card__btn" onClick={onSelect}>
        <div className="digest-card__top">
          {item.trigger_event_type && <span className="badge badge--trigger">{humanizeEventType(item.trigger_event_type)}</span>}
          {item.possible_rider?.label && <RiderBadge label={item.possible_rider.label} />}
          {item.sequence_surfacing && <SequenceBadge surfacing={item.sequence_surfacing} />}
          {item.tracking_score_before !== null && <span className="digest-card__score">{'skóre'} {item.tracking_score_before}</span>}
        </div>
        <div className="digest-card__title">{item.title}</div>
        {item.ranking_rule && (
          <div className="digest-card__reason">
            <code>{item.ranking_rule}</code>
          </div>
        )}
        <div className="digest-card__meta">{'Zařazeno:'} {formatDateTime(item.enqueued_at)}</div>
      </button>
    </li>
  );
}

function CompletedCard({ item, onSelect }: { item: LifecycleDigestCompleted; onSelect: () => void }) {
  const isAlert = item.alert === 'yes';

  return (
    <li className="digest-card">
      <button type="button" className="digest-card__btn" onClick={onSelect}>
        <div className="digest-card__top">
          <span className={`badge ${trackingLevelClass(item.final_risk_level)}`}>{trackingLevelLabel(item.final_risk_level)}</span>
          {isAlert && (
            <span className="badge badge--alert" aria-label={'Upozornění'}>
              {'⚠'} {'Upozornění'}
            </span>
          )}
          {item.possible_rider?.label && <RiderBadge label={item.possible_rider.label} />}
          {item.sequence_surfacing && <SequenceBadge surfacing={item.sequence_surfacing} />}
          <span className="digest-card__score">
            {item.tracking_score_before !== null && `${item.tracking_score_before} → `}
            {item.tracking_score_after}
            {item.tracking_score_delta !== null && <span className="digest-card__delta"> ({signedDelta(item.tracking_score_delta)})</span>}
          </span>
        </div>
        <div className="digest-card__title">{item.title}</div>
        <div className="digest-card__meta">{'Dokončeno:'} {formatDateTime(item.completed_at)}</div>
      </button>
    </li>
  );
}
